import * as Select from '@radix-ui/react-select';
import { startTransition, useEffect, useRef, useState } from 'react';

import { DropdownMenu } from '~/components/DropdownTreeSingleSelect/components/DropdownMenu';
import { DropdownTrigger } from '~/components/DropdownTreeSingleSelect/components/DropdownTrigger';
import { DROPDOWN_CLEAR_SENTINEL_VALUE } from '~/components/DropdownTreeSingleSelect/constants';
import { useTriggerZIndex } from '~/components/DropdownTreeSingleSelect/hooks/useTriggerZIndex';
import type {
  DropdownClearOptions,
  DropdownClearOptionsInternal,
  DropdownContentOptions,
  DropdownSearchOptions,
  DropdownState,
  DropdownTriggerVariant,
  ExternalDropdownOption,
  InternalDropdownOption,
  OptionTemplateComponent,
  RadixUiProps,
} from '~/components/DropdownTreeSingleSelect/types';
import { normalizeClearPlacements } from '~/components/DropdownTreeSingleSelect/utils/normalizeClearPlacements';
import { updateExternalToInternalRecursive } from '~/components/DropdownTreeSingleSelect/utils/updateExternalToInternalRecursive';
import { updateIsExpandedFalseRecursive } from '~/components/DropdownTreeSingleSelect/utils/updateIsExpandedFalseRecursive';
import { updateIsExpandedToggle } from '~/components/DropdownTreeSingleSelect/utils/updateIsExpandedToggle';
import { updateIsVisibleSearchRecursive } from '~/components/DropdownTreeSingleSelect/utils/updateIsVisibleSearchRecursive';
import { updateIsVisibleTrueRecursive } from '~/components/DropdownTreeSingleSelect/utils/updateIsVisibleTrueRecursive';
import { updateSelectedOptions } from '~/components/DropdownTreeSingleSelect/utils/updateSelectedOptions';

export interface DropdownTreeSingleSelectProps<TOptionProps = unknown>
  extends Select.SelectProps {
  id?: string;
  placeholder?: string;
  options: (ExternalDropdownOption<TOptionProps> & TOptionProps)[];
  contentOptions?: DropdownContentOptions;
  searchOptions?: DropdownSearchOptions;
  clearOptions?: DropdownClearOptions;
  showCheckIcon?: boolean;
  state?: DropdownState;
  variant?: DropdownTriggerVariant;
  // Provide an optional React component to show options with custom styling.
  // For instance, display the country flag next to the country name.
  OptionTemplate?: OptionTemplateComponent;
}

/**
 * DropdownTreeSingleSelect is built on top of Radix UI's Select, Accordion and Scroll Area primitives.
 * https://www.radix-ui.com/docs/primitives/components/select
 * https://www.radix-ui.com/docs/primitives/components/accordion
 * https://www.radix-ui.com/docs/primitives/components/scroll-area
 */
export function DropdownTreeSingleSelect<TOptionProps = unknown>({
  id,
  placeholder,
  options: externalOptions,
  contentOptions,
  searchOptions,
  clearOptions,
  showCheckIcon,
  OptionTemplate,
  state,
  variant = 'default',
  disabled,
  onOpenChange: parentOnOpenChange,
  ...select
}: DropdownTreeSingleSelectProps<TOptionProps>) {
  const [selectedOption, setSelectedOption] = useState<
    InternalDropdownOption | undefined
  >();

  const [options, setOptions] = useState<InternalDropdownOption[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const searchQueryTrimmed = searchQuery.trim();
  const searchQueryRef = useRef(searchQuery);
  const [isOpen, setIsOpen] = useState(
    select?.open || select?.defaultOpen || false,
  );
  const isOpenRef = useRef(isOpen);
  const [triggerRefCallback, triggerZIndex] = useTriggerZIndex(
    contentOptions?.zIndex,
  );
  const [selectedValue, setSelectedValue] = useState(
    select?.value || select?.defaultValue,
  );

  /**
   * Keep selectedValue in sync with select.value and select.defaultValue
   */
  useEffect(() => {
    setSelectedValue(select?.value || select?.defaultValue);
  }, [select?.value, select?.defaultValue]);

  /**
   * When the dropdown is closed, collapse all the expanded options except for the selected option
   * so that if/when the user re-opens the dropdown, the selected option is visible and easily
   * accessible in an otherwise potentially cluttered tree structure with many expanded branches.
   */
  useEffect(() => {
    if (!isOpen && isOpenRef.current && options.length) {
      setOptions((prevOptions) =>
        updateIsExpandedFalseRecursive(prevOptions, {
          keepExpandedIfHasSelected: true,
        }),
      );
    }
  }, [isOpen, options]);

  /**
   * When the dropdown is closed, clear the search query.
   */
  useEffect(() => {
    if (!isOpen && isOpenRef.current && searchQueryTrimmed) {
      setSearchQuery('');
    }
  }, [isOpen, searchQueryTrimmed]);

  /**
   * Remember the previous isOpen state so that we're only running certain useEffects
   * when isOpen changes and not when any of the other dependencies change.
   */
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  /**
   * When the searchQuery is cleared, collapse all expanded options.
   */
  useEffect(() => {
    if (
      isOpen &&
      !searchQueryTrimmed &&
      searchQueryRef.current &&
      options.length
    ) {
      setOptions((prevOptions) =>
        updateIsExpandedFalseRecursive(prevOptions, {
          keepExpandedIfHasSelected: true,
        }),
      );
    }
  }, [isOpen, options, searchQueryTrimmed]);

  /**
   * Translate the barebones external options object and augment it with the data structure we use
   * internally to manage the state of the tree structure for the dropdown.
   */
  useEffect(() => {
    const { internalOptions, selectedOption: initialSelectedOption } =
      updateExternalToInternalRecursive(externalOptions, {
        optionIndexPath: [],
        selectedValue: selectedValue,
      });

    setOptions(internalOptions);
    setSelectedOption(initialSelectedOption);
  }, [externalOptions, selectedValue]);

  /**
   * When the searchQuery changes, update the options to show only the options
   * that match the search query.
   */
  useEffect(() => {
    // If the search query hasn't changed, do nothing.
    if (searchQueryTrimmed === searchQueryRef.current) {
      return;
    }

    if (searchQueryTrimmed) {
      setOptions((prevOptions) =>
        updateIsVisibleSearchRecursive(prevOptions, { searchQueryTrimmed }),
      );

      return;
    }

    setOptions(updateIsVisibleTrueRecursive);
  }, [searchQueryTrimmed]);

  /**
   * Remember the previous searchQuery value so that we're only running certain useEffects
   * when the searchQuery changes.
   */
  useEffect(() => {
    searchQueryRef.current = searchQueryTrimmed;
  }, [searchQueryTrimmed]);

  const handleSearchQueryChange = (query: string) => {
    setSearchQuery(query);
  };

  const handleSelectOption = (newSelectedOption: InternalDropdownOption) => {
    setSelectedOption((prevSelectedOption) => {
      // If this option is already the selected option, do nothing.
      if (prevSelectedOption?.value === newSelectedOption.value) {
        return prevSelectedOption;
      }

      // Strip the "children" options before updating the selected option,
      // because it's unnecessary and potentially wasteful in terms of memory.
      const { children: _children, ...newOptionWithoutChildren } =
        newSelectedOption;

      // Update options based on previous selected option.
      setOptions((prevOptions) =>
        updateSelectedOptions(prevOptions, {
          prevSelectedOption,
          newSelectedOption,
        }),
      );

      return newOptionWithoutChildren;
    });
  };

  /**
   * Set the "isExpanded" value for the option that is being expanded or collapsed.
   * @param option - The option to expand or collapse.
   * @param isExpanded - Boolean value for the option that is expanding or collapsing.
   */
  const handleExpandOption = (
    option: InternalDropdownOption,
    isExpanded: boolean,
  ) => {
    setOptions((prevOptions) =>
      updateIsExpandedToggle(prevOptions, { option, isExpanded }),
    );
  };

  const handleClear = () => {
    startTransition(() => setIsOpen(false));

    if (isOpenRef.current) {
      parentOnOpenChange?.(false);
    }

    setOptions((prevOptions) =>
      updateSelectedOptions(prevOptions, {
        prevSelectedOption: selectedOption,
        newSelectedOption: undefined,
      }),
    );
    setSelectedOption(undefined);
    setSelectedValue(undefined);

    clearOptions?.onClear?.();
  };

  const clearOptionsInternal: DropdownClearOptionsInternal = {
    ...clearOptions,
    placements: normalizeClearPlacements(clearOptions?.placements),
    isEnabled:
      clearOptions?.isEnabled &&
      (select?.value === undefined || !!clearOptions?.onClear),
    onClear: handleClear,
  };

  const radixWithSelect: RadixUiProps['radix'] = {};

  return (
    <Select.Root
      {...select}
      open={isOpen}
      onOpenChange={(newIsOpen) => {
        startTransition(() => {
          setIsOpen(newIsOpen);
        });
        parentOnOpenChange?.(newIsOpen);
      }}
      onValueChange={(value) => {
        // Radix guarantees (and throws an error) that '' isn't a valid value.
        // Guard against empty-string onValueChange overriding form state when
        // multiple selects reference the same field (see WMG1-1078).
        if (value === '') {
          return;
        }

        // The pinned "Clear selection" row is rendered as a Select.Item with a
        // sentinel value so it joins Radix's roving focus / typeahead. Route
        // it through handleClear and never propagate the sentinel to consumers.
        if (value === DROPDOWN_CLEAR_SENTINEL_VALUE) {
          handleClear();

          return;
        }

        select.onValueChange?.(value);
      }}
      disabled={disabled}
    >
      <DropdownTrigger
        variant={variant}
        ref={triggerRefCallback}
        id={id}
        placeholder={placeholder}
        selectedOption={selectedOption}
        isOpen={isOpen}
        state={state}
        disabled={disabled}
        OptionTemplate={OptionTemplate}
        clearOptions={clearOptionsInternal}
        radix={radixWithSelect}
      />
      {isOpen ? (
        <DropdownMenu
          options={options}
          contentOptions={{
            ...contentOptions,
            zIndex: triggerZIndex,
          }}
          searchOptions={searchOptions}
          clearOptions={clearOptionsInternal}
          searchQuery={searchQuery}
          onSearchQueryChange={handleSearchQueryChange}
          selectedOption={selectedOption}
          onSelectOption={handleSelectOption}
          onExpandOption={handleExpandOption}
          showCheckIcon={showCheckIcon}
          OptionTemplate={OptionTemplate}
          radix={radixWithSelect}
        />
      ) : null}
    </Select.Root>
  );
}

DropdownTreeSingleSelect.displayName = 'DropdownTreeSingleSelect';
